import { useState, useEffect, useRef } from 'react';

export default function ApproachSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      title: 'Discovery & Immersion',
      description: 'Understanding your business, audience, and market.',
      icon: 'ri-search-line',
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Strategy & Positioning',
      description: 'Defining your brand\'s core purpose and value proposition.',
      icon: 'ri-lightbulb-line',
      color: 'from-purple-600 to-pink-500'
    },
    {
      title: 'Messaging & Voice',
      description: 'Crafting a unique brand voice and tone.',
      icon: 'ri-mic-line',
      color: 'from-pink-500 to-pink-600'
    },
    {
      title: 'Visual Identity',
      description: 'Establishing color palettes, typography, and style.',
      icon: 'ri-palette-line',
      color: 'from-pink-600 to-orange-500'
    },
    {
      title: 'Brand Guidelines',
      description: 'Delivering a consistent framework for your brand\'s application.',
      icon: 'ri-book-line',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden"
    >
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url('/assets/brand-process-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/70"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            A Collaborative Journey from 
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Vision to Identity</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            At Hash Adz, our process blends strategic foresight with creativity. We begin by immersing ourselves 
            in your world, using insights and expertise to craft a powerful brand strategy that guides all your 
            future marketing and product efforts.
          </p>
        </div>

        {/* Process Flow */}
        <div className="relative">
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 transform -translate-y-1/2"></div>
          
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative group cursor-pointer transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
                onMouseEnter={() => setActiveStep(index)}
              >
                {/* Step Number */}
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white font-bold text-xl relative z-10 group-hover:scale-110 transition-transform duration-300`}>
                    {index + 1}
                  </div>
                </div>

                {/* Step Content */}
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-purple-500/20 group-hover:border-purple-500/50 transition-all duration-300 group-hover:transform group-hover:scale-105">
                  <div className="text-center">
                    <div className={`w-12 h-12 mx-auto rounded-lg bg-gradient-to-r ${step.color} flex items-center justify-center mb-4 group-hover:animate-pulse`}>
                      <i className={`${step.icon} text-xl text-white`}></i>
                    </div>
                    
                    <h3 className="text-lg font-bold text-white mb-3 group-hover:text-purple-400 transition-colors duration-300">
                      {step.title}
                    </h3>
                    
                    <p className="text-gray-300 group-hover:text-white transition-colors duration-300 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Arrow for mobile */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center mt-4">
                    <i className="ri-arrow-down-line text-purple-400 text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}